import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { TravelExplore as TravelExploreIcon } from '@mui/icons-material';

interface PlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
}

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  value,
  onChange,
  label = 'Place of Birth',
  placeholder = 'Start typing a city...',
  size = 'medium',
}) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState(value);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setSuggestions([]);
      return;
    }

    const google = (window as any).google;
    if (!google?.maps?.places?.AutocompleteSuggestion) return;

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    }

    const timer = setTimeout(async () => {
      try {
        const { suggestions: results } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: inputValue,
          includedPrimaryTypes: ['locality', 'administrative_area_level_3', 'sublocality'],
          sessionToken: sessionTokenRef.current,
          language: 'en',
        });

        if (results?.length) {
          setSuggestions(
            results
              .filter((s: any) => s.placePrediction)
              .map((s: any) => s.placePrediction.text.text),
          );
        } else {
          setSuggestions([]);
        }
      } catch {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  return (
    <Autocomplete
      freeSolo
      options={suggestions}
      value={value}
      onChange={(_, newValue) => {
        onChange(newValue || '');
        sessionTokenRef.current = null;
      }}
      inputValue={inputValue}
      onInputChange={(_, newInput) => {
        setInputValue(newInput);
        if (!suggestions.includes(newInput)) onChange(newInput);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          InputProps={{
            ...params.InputProps,
            startAdornment: <TravelExploreIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 20 }} />,
          }}
        />
      )}
      noOptionsText="Type at least 2 characters..."
    />
  );
};

export default PlacesAutocomplete;
